import React from "react";
import styles from "./PDFExport.module.scss";
import { PesTest, TrainedIndicator } from "generated/graphql";
import { PartialDeep } from "type-fest";
import { format, parseISO } from "date-fns";
import {
  GetDynamicValue,
  GetPESIndex,
  IndicatorNameMatches,
} from "@pages/pdf-export/Indices";
import tinygradient from "tinygradient";
import { IndicatorGradientColors } from "@pages/pdf-export/PDFExport";
import type { PdfIndicatorHeader } from "@pages/pdf-export/Page1Table";

interface Page4Props {
  allTests: PartialDeep<PesTest, { recurseIntoArrays: true }>[];
  indicatorNamesAndIds: {
    id: string;
    name: string;
    displayName: string;
    trainedIndicator: PartialDeep<TrainedIndicator>;
  }[];
  /** Headers to render; defaults to every carbon/nitrogen indicator. */
  headers?: PdfIndicatorHeader[];
}

export const page4Headers: PdfIndicatorHeader[] = [
  {
    id: "omDumas",
    displayName: "Soil Organic Matter (Dumas)",
    name: "OM Dumas",
  },
  {
    id: "totalNitrogen",
    displayName: "Total Nitrogen",
    name: "Total nitrogen",
  },
  {
    id: "totalCarbon",
    displayName: "Total Carbon",
    name: "Total carbon",
  },
  {
    id: "soilOrganicCarbon",
    displayName: "Soil Organic Carbon",
    name: "Soil organic carbon",
  },
  {
    id: "cnRatio",
    displayName: "C:N Ratio",
    name: "CN ratio",
  },
  {
    id: "bulkDensity",
    displayName: "Bulk Density",
    name: "Bulk density",
  },
];

const Page4: React.FC<Page4Props> = ({
  allTests,
  indicatorNamesAndIds,
  headers: carbonNitrogenHeaders = page4Headers,
}) => {
  const gradient = tinygradient(IndicatorGradientColors);

  const findTrainedIndicator = (indicatorName: string) => {
    return indicatorNamesAndIds.find(
      (indicator) => IndicatorNameMatches(indicator.name, indicatorName)
    )?.trainedIndicator;
  };

  const getValueHeader = (indicatorName: string) => {
    const unit = findTrainedIndicator(indicatorName)?.indicatorDefinition?.unit;

    return unit ? `Value in ${unit}` : "Value";
  };

  return (
    <div className={styles.reportTableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.infoTable}>
          <thead>
            <tr>
              <th rowSpan={2} className={styles.tableHeader}>
                Sample Name
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Farm
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Field
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                What3Words
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Date of Test
              </th>
              {carbonNitrogenHeaders.map((header) => (
                <th key={header.id} className={styles.subHeader} colSpan={2}>
                  {header.displayName}
                </th>
              ))}
            </tr>
            <tr>
              {carbonNitrogenHeaders.map((header) => (
                <React.Fragment key={header.id}>
                  <th key={`${header.id}-value`} className={styles.subHeader}>
                    {getValueHeader(header.name)}
                  </th>
                  <th key={`${header.id}-index`} className={styles.subHeader}>
                    PES index
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTests.map((test) => (
              <tr key={test.id} className={styles.tableRow}>
                <td className={styles.tableCell}>{test.name}</td>
                <td className={styles.tableCell}>{test.farm?.name}</td>
                <td className={styles.tableCell}>{test.field?.name}</td>
                <td className={`${styles.tableCell} ${styles.noBreak}`}>
                  {test.location
                    ? `${test.location.what3Words?.words ?? "Unknown"}`
                    : "N/A"}
                </td>
                <td className={styles.tableCell}>
                  {test.createdAt
                    ? format(parseISO(test.createdAt), "dd/MM/yyyy")
                    : "N/A"}
                </td>
                {carbonNitrogenHeaders.map((header) => {
                  const { value } = GetDynamicValue(test, header.name);
                  const trainedIndicator = findTrainedIndicator(header.name);
                  const pesIndex = GetPESIndex(value, trainedIndicator);
                  const index = pesIndex
                    ? pesIndex.indices.names[pesIndex.indexValue]
                    : "N/A";
                  const divisor = pesIndex
                    ? pesIndex.indices.names.length - 1
                    : 0;
                  const gradientPosition = Math.min(
                    Math.max(
                      0,
                      divisor === 0 || !pesIndex
                        ? 0
                        : pesIndex.indexValue / divisor
                    ),
                    1
                  );
                  const indexColor =
                    index === "N/A"
                      ? undefined
                      : gradient
                          .rgbAt(gradientPosition)
                          .setAlpha(0.7)
                          .toHex8String();

                  return (
                    <React.Fragment key={header.id}>
                      {test.isFailure ? (
                        <>
                          <td className={styles.tableCell}>N/A</td>
                          <td className={styles.tableCell}>N/A</td>
                        </>
                      ) : (
                        <>
                          <td className={styles.tableCell}>
                            {typeof value === "number"
                              ? value.toFixed(
                                  trainedIndicator?.indicatorDefinition
                                    ?.displayDecimalPlaces ?? 2
                                )
                              : value}
                          </td>
                          <td
                            className={`${styles.tableCell} ${
                              indexColor &&
                              !gradient.rgbAt(gradientPosition).isLight()
                                ? styles.light
                                : ""
                            }`}
                            style={{ backgroundColor: indexColor }}
                          >
                            {index}
                          </td>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Page4;
